<template>
	<div class="order-history-container">
		<ModalHeader
			class="modal-header"
			:title="modalHeaderTitle"
		>
			<template v-slot:extra-buttons>
				<p class="txt-title-2" v-if="order">{{ getString('total') }}: {{ currencyStore.readableCurrencyValue(order.grandTotal) }}</p>
			</template>
		</ModalHeader>
		<div v-show="loading" class="loading-container">
			<AppLoader :size="40" primary />
		</div>

		<div v-show="!loading">
			<AppTable
				ref="logs-table"
				:fetch-function="getOrderHistory"
				:filters="filters"
				:table-headings="tableHeadings"
				:title="getString('order_history')"
				:items="logs || []"
				initial-sort-field="date"
			>
				<template v-slot:custom-pagination>
					<div class="row space-between centered">
						<div class="go-back go-direction" @click="currNextKeyIndex > 0 ? getOrderHistory({before: true}) : null">
							<AppChevronIcon v-if="currNextKeyIndex > 0" direction="left" />
						</div>
						<div class="go-forward go-direction" @click="currNextKeyIndex <= nextKeys.length-1 ? getOrderHistory({after: true}) : null">
							<AppChevronIcon v-if="currNextKeyIndex <= nextKeys.length-1" direction="right" />
						</div>
					</div>
				</template>
				<template v-slot:details-string="{item}">
					<div class="details-string-container" v-text="item.detailsString" />
				</template>
			</AppTable>
		</div>
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useCurrencyStore, useNotificationStore, useValuesStore} from "@/shared/stores";
import {useUserStore, useOrdersStore} from "@/console-new/stores";
import API from "@/shared/helpers/API";
import {getCompatibleDate} from "@/shared/helpers/dates";

export default {
	setup() {
		const showNotification = useNotificationStore().showNotification;
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		const ordersStore = useOrdersStore();
		const valuesStore = useValuesStore();
		return {
			showNotification,
			ordersStore,
			languageStore,
			getString: languageStore.getString,
			userStore,
			currencyStore,
			valuesStore,
		}
	},
	name: "OrderHistory",
	components: {AppTable, AppChevronIcon, ModalHeader, AppLoader},
	props: {
		order: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			logs: [],
			loading: true,
			tableHeadings: [
				{
					text: this.getString('date'),
					cols: 1,
					fieldName: 'date',
					formatter: this.formatDate,
				},
				{
					text: this.getString('user'),
					cols: 1,
					fieldName: 'user_name',
				},
				{
					text: this.getString('type'),
					cols: 3,
					fieldName: 'typeString',
				},
				{
					text: this.getString('details'),
					cols: 3,
					fieldName: 'detailsString',
					slotName: 'details-string',
				},
			],
			filters: null,
			nextKeys: [],
			currNextKeyIndex: 0,
		}
	},
	computed: {
		modalHeaderTitle() {
			if(!this.order?.order_number_day) {
				return '';
			}
			let orderNumber = this.order?.order_number_day.toString();
			let zeroesToAdd = 3 - orderNumber.length;
			for(let i=0; i<zeroesToAdd; i++) {
				orderNumber = '0' + orderNumber;
			}
			return this.getString('order_x', ['#'+orderNumber]);
		},
		statusPhrase() {
			if (!this.orderIsCompleted) {
				return this.getString(this.order?.status)
			}
			return this.order.paymentMethodName;
		},
		orderIsCompleted() {
			return this.order?.status === this.ordersStore.STATUS.completed || this.order?.status === this.ordersStore.STATUS.canceled || this.order?.status === this.ordersStore.STATUS.failed;
		},
	},
	methods: {
		getOrderHistory(filters, callback = null) {
			if (!this.userStore.currBusinessId || !this.order) {
				if (callback) {
					callback(false);
				}
				this.loading = false;
				return;
			}

			this.loading = true;
			let lastKeyToUse = null;
			if(filters && filters.after) {
				if(this.currNextKeyIndex < this.nextKeys.length) {
					lastKeyToUse = this.nextKeys[this.currNextKeyIndex];
					this.currNextKeyIndex++;
				} else {
					lastKeyToUse = null;
					this.currNextKeyIndex = 0;
					this.nextKeys = [];
				}
			} else if(filters && filters.before) {
				if(this.currNextKeyIndex > 1) {
					lastKeyToUse = this.nextKeys[this.currNextKeyIndex - 2];
					this.currNextKeyIndex--;
				} else if(this.currNextKeyIndex > 0) {
					lastKeyToUse = null;
					this.currNextKeyIndex--;
				} else {
					lastKeyToUse = null;
					this.currNextKeyIndex = 0;
					this.nextKeys = [];
				}
			} else {
				lastKeyToUse = filters && filters.last_key ? filters.last_key : null;
				this.currNextKeyIndex = 0;
				this.nextKeys = [];
			}
			let currNextKeyIndex = this.currNextKeyIndex;

			API.init().getOrderHistory(this.userStore.currBusinessId, this.order.order_id, lastKeyToUse, r => {
				this.logs = [];
				if (r && r.result === "OK") {
					if (r.logs && r.logs && r.logs.length) {
						let users = r.users || {};
						r.logs.forEach(t => {
							let log = this.normalizeLog(t, users);
							if(log) {
								this.logs.push(log);
							}
						});
						if(r.last_key && currNextKeyIndex === this.currNextKeyIndex && this.currNextKeyIndex === this.nextKeys.length) {
							this.nextKeys.push(JSON.stringify(r.last_key));
						}
					}
					this.loading = false;
					if (callback) {
						callback(true);
					}
				} else {
					this.loading = false;
					this.showNotification('generic-error');
					if (callback) {
						callback(false, r ? r.details : null);
					}
				}
				this.$nextTick(()=>{
					// Made this since on first load it was detecting table width = 0
					let logTable = this.$refs['logs-table'];
					if(logTable && logTable.initTableWidth) {
						logTable.initTableWidth();
					}
				})
			});
		},
		normalizeLog(log, users) {
			let timeEvent = log.time_event && log.time_event.S ? log.time_event.S.split("#") : [];
			if(timeEvent.length >= 3) {
				let date = new Date(parseInt(timeEvent[2]));
				log.date = getCompatibleDate(date);
				if(log.user_id && log.user_id.N) {
					let userId = Number(log.user_id.N);
					if(userId && users.hasOwnProperty(userId)) {
						log.user_name = users[userId];
					}
				}
				log.user_name = log?.user_name || "-";
				if(log.type && log.type.S) {
					log.type = log.type.S;
				} else {
					log.type = "-";
				}
				log.typeString = this.getActionLabel(log.type, log?.data);
				log.detailsString = this.getActionValue(log.type, log?.data);
				return log;
			} else {
				return null;
			}
		},
		formatDate(dateString) {
			if (!dateString) return '-';
			const lang = this.languageStore.lang || 'it-IT';
			return getCompatibleDate(dateString).toLocaleDateString([lang], {year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute:'2-digit', second:'2-digit', hour12: false});
		},
		getActionLabel(type, data = null) {
			const actionMap = {
				'order_create': this.getString('order_created'),
				'order_confirm': this.getString('order_confirmed'),
				'order_accept': this.getString('order_accepted'),
				'order_prepare': this.getString('order_preparing'),
				'order_prepare_done': this.getString('order_prepared'),
				'order_complete': this.getString('order_completed'),
				'order_cancel': this.getString('order_canceled'),
				'order_cancel_partial': this.getString('order_canceled_partial'),
				'order_refuse': this.getString('order_refused'),
				'order_pay': this.getString('order_paid'),
				'order_pay_partial': this.getString('partial_payment'),
				'order_edit': this.getString('order_edited'),
				'order_unite': this.getString('orders_united'),
				'item_redeemed': this.getString('item_redeemed'),
				'send_sms': this.getString('notification_message_sent'),
				'order_next': this.getString('order_next'),
				'order_fiscal_request': this.getString('order_fiscal_request'),
				'order_fiscal_receipt': this.getString('order_fiscal_receipt'),
				'payment_fiscal_request': this.getString('order_fiscal_request'),
				'payment_fiscal_receipt': this.getString('order_fiscal_receipt'),
				'payment_invoice_linked': this.getString('payment_invoice_linked'),
				'order_invoice_linked': this.getString('order_invoice_linked'),
			};
			return actionMap[type] || type;
		},
		getActionValue(type, data = null) {
			if(type === 'order_next' && data && data?.S) {
				try {
					return this.getString('order_next_param', [data.S]);
				} catch(e) {
					console.log('Next course log without course number, fallback to generic message');
				}
			} else if(type === 'order_create' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					let orderNumber = parsedData.order_number_day.toString();
					let zeroesToAdd = 3 - orderNumber.length;
					for(let i=0; i<zeroesToAdd; i++) {
						orderNumber = '0' + orderNumber;
					}
					return '#'+orderNumber;
				} catch(e) {
					console.log('Order creation log without order number, fallback to generic message');
				}
			} else if(type === 'order_edit' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					return parsedData.join("\n");
				} catch(e) {
					console.log('Order edit log without any edit, fallback to generic message');
				}
			} else if(type === 'order_unite' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					let returningString = '';
					if(parsedData && parsedData?.united_quantity) {
						returningString = this.getString('orders_num', [parsedData.united_quantity]);
					}
					if(parsedData && parsedData?.united_table_name) {
						returningString = returningString ? returningString + ' - ' : '';
						returningString += parsedData.united_table_name;
					}
					return returningString;
				} catch(e) {
					console.log('Order unite without any log data, fallback to generic message');
				}
			} else if(type === 'order_pay_partial' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					let details = [];
					if(parsedData?.payment_type) {
						let paymentTypeName = this.valuesStore.paymentsMethods[parsedData.payment_type]?.name || parsedData.payment_type;
						if(paymentTypeName) {details.push(this.getString('payment_method_x',[paymentTypeName]))}
					}
					if(parsedData?.payment_amount) {
						let paymentAmountString = this.getString('transaction_amount', [this.currencyStore.readableCurrencyValue(parsedData.payment_amount)]);
						if(parsedData?.order_payment_amount) {
							if (Number(parsedData.payment_amount) !== Number(parsedData.order_payment_amount)) {
								let transactionAmount = this.currencyStore.readableCurrencyValue(parsedData.payment_amount);
								let allocatedAmount = this.currencyStore.readableCurrencyValue(parsedData.order_payment_amount);
								paymentAmountString = this.getString('transaction_allocated_amount', [transactionAmount, allocatedAmount]);
							}
						}
						details.push(paymentAmountString);
					}
					if(parsedData?.total_paid) {
						details.push(this.getString('total_paid', [this.currencyStore.readableCurrencyValue(parsedData.total_paid)]));
					}
					if(parsedData?.outstanding) {
						details.push(this.getString('outstanding', [this.currencyStore.readableCurrencyValue(parsedData.outstanding)]));
					}
					return details.join("\n");
				} catch(e) {
					console.log('Partial payment log without data, fallback to generic message', e);
				}
			} else if(type === 'item_redeemed' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					if(parsedData?.quantity) {
						return parsedData.quantity + 'x' +' '+ (parsedData?.item_name || '');
					}
				} catch(e) {
					console.log('Item redeemed log without data, fallback to generic message');
				}
			} else if(type === 'order_pay' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					if(parsedData?.payment_type) {
						let paymentType = this.valuesStore.paymentsMethods[parsedData.payment_type]?.name || parsedData.payment_type;
						return this.getString('payment_method_x', [paymentType]);
					}
				} catch(e) {
					console.log('Order payment log without data, fallback to generic message');
				}
			} else if(type === 'send_sms' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					let details = [];
					if(parsedData?.type) {
						details.push(this.getString('type_x', [parsedData.type]));
					}
					if(parsedData?.phone) {
						details.push(this.getString('phone_x', [parsedData.phone]));
					}
					return details.join("\n");
				} catch(e) {
					console.log('Order sms log without data, fallback to generic message');
				}
			} else if(type === 'payment_fiscal_request' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					if(parsedData?.payment_amount) {
						let paymentAmountString = this.getString('receipt_requested_for', [this.currencyStore.readableCurrencyValue(parsedData.payment_amount)]);
						if(parsedData?.order_payment_amount) {
							if (Number(parsedData.payment_amount) !== Number(parsedData.order_payment_amount)) {
								let transactionAmount = this.currencyStore.readableCurrencyValue(parsedData.payment_amount);
								let allocatedAmount = this.currencyStore.readableCurrencyValue(parsedData.order_payment_amount);
								paymentAmountString = this.getString('allocated_receipt_requested_for', [transactionAmount, allocatedAmount]);
							}
						}
						return paymentAmountString;
					}

				} catch(e) {
					console.log('Payment fiscal request log without data, fallback to generic message');
				}
			} else if(type === 'payment_fiscal_receipt' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					if(parsedData?.payment_amount) {
						let paymentAmountString = this.getString('receipt_emitted_for', [this.currencyStore.readableCurrencyValue(parsedData.payment_amount)]);
						if(parsedData?.order_payment_amount) {
							if (Number(parsedData.payment_amount) !== Number(parsedData.order_payment_amount)) {
								let transactionAmount = this.currencyStore.readableCurrencyValue(parsedData.payment_amount);
								let allocatedAmount = this.currencyStore.readableCurrencyValue(parsedData.order_payment_amount);
								paymentAmountString = this.getString('allocated_receipt_emitted_for', [transactionAmount, allocatedAmount]);
							}
						}
						return paymentAmountString;
					}
				} catch(e) {
					console.log('Payment fiscal receipt log without data, fallback to generic message');
				}
			} else if(type === 'payment_invoice_linked' && data && data?.S) {
				try {
					let parsedData = JSON.parse(data.S);
					if(parsedData?.payment_amount) {
						let paymentAmountString = this.getString('invoice_emitted_for', [this.currencyStore.readableCurrencyValue(parsedData.payment_amount)]);
						if(parsedData?.order_payment_amount) {
							if (Number(parsedData.payment_amount) !== Number(parsedData.order_payment_amount)) {
								let transactionAmount = this.currencyStore.readableCurrencyValue(parsedData.payment_amount);
								let allocatedAmount = this.currencyStore.readableCurrencyValue(parsedData.order_payment_amount);
								paymentAmountString = this.getString('allocated_invoice_emitted_for', [transactionAmount, allocatedAmount]);
							}
						}
						return paymentAmountString;
					}
				} catch(e) {
					console.log('Payment invoice log without data, fallback to generic message');
				}
			}

			return '-';
		},
	},
}
</script>

<style scoped lang="scss">
.order-history-container {
	.loading-container {
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;
		padding: 60px 20px;
		gap: 20px;
	}
	.modal-header {
		margin-bottom: 20px;
	}
	.order-info {
		padding: 12px;
		background: var(--color-background-secondary);
		border-radius: 8px;

		h3 {
			margin: 0 0 8px 0;
			color: var(--color-text-primary);
		}

		.order-details {
			display: flex;
			gap: 16px;
			font-size: 14px;
			color: var(--color-text-secondary);

			span {
				display: flex;
				align-items: center;
			}
		}
	}
}

.go-direction {
	cursor: pointer;
	padding: 8px;
}
.details-string-container {
	max-width: 100%;
	overflow-wrap: break-word;
	hyphens: auto;
	font-size: 12px;
	line-height: 16px;
	white-space: pre-line;
}
</style>